/* eslint-disable react/prop-types */
/**
 * MobileMoreDrawer (Iter 290l)
 *
 * The 6th slot on the mobile bottom nav opens this full-height
 * slide-up drawer. Its content tree is THE SAME data the desktop
 * sidebar renders from — every pillar + its children + Tools +
 * always-on top items. Single source of truth so the moment a new
 * page is added to a pillar (`PILLAR_CHILDREN.<x>`) it appears on
 * BOTH surfaces with zero extra wiring.
 *
 * Trust Contract: NO `<dialog>` / portal modal. Renders as a
 * full-height fixed panel that obeys outside-tap + Esc + Back. Body
 * scroll-locks while open.
 *
 * Props
 * -----
 *   open       : boolean
 *   onClose    : fn
 *   sections   : [{ key, label, icon, items: [{ to, label, icon, key, badge? }] }]
 *   onNavigate : fn(to) — defaults to <Link>-style navigation
 *
 * The search input filters items across ALL sections by label
 * (case-insensitive substring). Sections with zero matches hide
 * during search. Recent destinations are stored under
 * `hv:mobile_more_recents` and surface in the top "Recent" section
 * when the search is empty.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, Clock as ClockIcon, ChevronRight } from "lucide-react";

const RECENTS_KEY = "hv:mobile_more_recents";
const RECENTS_MAX = 6;


function readRecents() {
  try {
    const raw = localStorage.getItem(RECENTS_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.slice(0, RECENTS_MAX) : [];
  } catch {
    return [];
  }
}


function writeRecents(item) {
  try {
    const cur = readRecents().filter((r) => r.to !== item.to);
    cur.unshift({ to: item.to, label: item.label });
    localStorage.setItem(RECENTS_KEY, JSON.stringify(cur.slice(0, RECENTS_MAX)));
  } catch {
    // localStorage may be disabled; recents are best-effort only.
  }
}


export default function MobileMoreDrawer({
  open,
  onClose,
  sections = [],
}) {
  const [query, setQuery] = useState("");
  const [recents, setRecents] = useState([]);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Esc / Back closes; body scroll-lock while open.
  useEffect(() => {
    if (!open) return undefined;
    setRecents(readRecents());
    setQuery("");
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    // Focus search after the open transition.
    const t = setTimeout(() => inputRef.current?.focus(), 250);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
      clearTimeout(t);
    };
  }, [open, onClose]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sections;
    return sections
      .map((s) => ({
        ...s,
        items: (s.items || []).filter(
          (it) =>
            (it.label || "").toLowerCase().includes(q) ||
            (it.to || "").toLowerCase().includes(q),
        ),
      }))
      .filter((s) => (s.items || []).length > 0);
  }, [query, sections]);

  const allItems = useMemo(() => {
    const flat = [];
    sections.forEach((s) => (s.items || []).forEach((it) => flat.push(it)));
    return flat;
  }, [sections]);

  const recentItems = useMemo(() => {
    if (!recents.length) return [];
    return recents
      .map((r) => allItems.find((it) => it.to === r.to))
      .filter(Boolean);
  }, [recents, allItems]);

  const handleGo = (item) => {
    writeRecents(item);
    onClose?.();
    if (item.external) {
      window.open(item.to, "_blank", "noopener");
    } else {
      navigate(item.to);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[80] lg:hidden"
      data-testid="mobile-more-drawer"
      role="dialog"
      aria-modal="true"
      aria-label="More navigation"
    >
      {/* Backdrop */}
      <button
        type="button"
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        aria-label="Close menu"
        data-testid="mobile-more-backdrop"
      />
      {/* Panel */}
      <div className="absolute inset-x-0 bottom-0 top-12 bg-[#0f1116] border-t border-white/10 flex flex-col rounded-t-2xl hv-safe-bottom">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-white/5">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#647087]" />
            <input
              ref={inputRef}
              type="search"
              inputMode="search"
              autoComplete="off"
              spellCheck={false}
              placeholder="Search every page…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="mobile-more-search"
              className="w-full pl-9 pr-3 py-2 rounded-lg bg-[#1a1c22] text-[13px] text-white placeholder:text-[#647087] focus:outline-none focus:ring-1 focus:ring-[#1fb6c1]"
            />
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="mobile-more-close"
            className="p-2 text-[#a0aabf] hover:text-white"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-3 py-3 space-y-5">
          {/* Recents — hidden during search to avoid noise */}
          {!query && recentItems.length > 0 && (
            <Section
              label="Recent"
              icon={ClockIcon}
              items={recentItems}
              onGo={handleGo}
              testIdPrefix="mobile-more-recent"
            />
          )}

          {filtered.length === 0 && (
            <p
              className="text-center text-sm text-[#647087] py-8"
              data-testid="mobile-more-empty"
            >
              Nothing matches "{query}". Try another word.
            </p>
          )}

          {filtered.map((s) => (
            <Section
              key={s.key}
              label={s.label}
              icon={s.icon}
              items={s.items}
              onGo={handleGo}
              testIdPrefix={`mobile-more-${s.key}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}


function Section({ label, icon: Icon, items, onGo, testIdPrefix }) {
  return (
    <section data-testid={`${testIdPrefix}-section`}>
      <div className="flex items-center gap-2 px-2 mb-1.5 text-[10px] uppercase tracking-wider text-[#647087]">
        {Icon ? <Icon className="w-3.5 h-3.5" strokeWidth={1.6} /> : null}
        <span>{label}</span>
      </div>
      <ul className="space-y-1">
        {items.map((it) => (
          <li key={it.key || it.to}>
            <button
              type="button"
              onClick={() => onGo(it)}
              data-testid={`${testIdPrefix}-${it.key || it.to.replace(/[^a-z0-9]+/gi, "-")}`}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg bg-white/[0.03] hover:bg-white/[0.07] active:bg-white/10 text-left text-[#e2e8f0] transition-colors"
            >
              {it.icon ? <it.icon className="w-4 h-4 text-[#a0aabf] flex-shrink-0" strokeWidth={1.7} /> : null}
              <span className="flex-1 text-[13px] leading-tight">{it.label}</span>
              <ChevronRight className="w-4 h-4 text-[#647087] flex-shrink-0" />
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
